import type { InjectionKey } from 'vue'
import type { OnlineStore } from '~/composables/useOnlineStores'
import { hasStorePermission } from '~/composables/store/permissions'
import { readStoreOwnerMode } from '~/composables/useStoreEditorArmed'

/**
 * Owner-only "live preview" toggle. When enabled, the storefront hides every
 * inline editor / owner-only control so the owner sees the page exactly as a
 * visitor would. Shared across pages via useState so the Navbar toggle and the
 * per-page `isOwner` checks stay in sync during client-side navigation.
 */
const LIVE_PREVIEW_BODY_CLASS = 'store-live-preview'
let livePreviewBodyBound = false

export function useStoreLivePreview() {
  // Cookie-backed so SSR matches a remembered "View as" session.
  const live = useState<boolean>('store-live-preview', () => readStoreOwnerMode() === 'view')

  // Keep a body class in sync so CSS can strip trainer rings even if a
  // component still applies an `is-trainer-*` class for a frame.
  if (import.meta.client && !livePreviewBodyBound) {
    livePreviewBodyBound = true
    watch(
      live,
      (on) => {
        document.body.classList.toggle(LIVE_PREVIEW_BODY_CLASS, on)
      },
      { immediate: true },
    )
  }

  return live
}

/**
 * Thumbnail / layout-picker copies of live storefront elements. Trainer rings
 * belong on the page, not on these previews.
 */
const STORE_THUMBNAIL_PREVIEW_KEY: InjectionKey<boolean> = Symbol('store-thumbnail-preview')

export function provideStoreThumbnailPreview() {
  provide(STORE_THUMBNAIL_PREVIEW_KEY, true)
}

/** False while live preview is on, or until the owner starts this page's lessons. */
export function useTrainerVisualsEnabled() {
  const livePreview = useStoreLivePreview()
  const lessonsActive = useState('store-trainer-lessons-active', () => false)
  const inThumbnailPreview = inject(STORE_THUMBNAIL_PREVIEW_KEY, false)
  return computed(() => !livePreview.value && !inThumbnailPreview && lessonsActive.value)
}

export function toggleStoreLivePreview() {
  const live = useStoreLivePreview()
  live.value = !live.value
}

const MOBILE_CANVAS_CLASS = 'store-preview-device-mobile'
const MOBILE_CANVAS_KEY = 'cs.storeMobileCanvas.v1'

/**
 * Owner "phone canvas" toggle. Shrinks `.store-site` to a phone column and
 * flips storefront CSS onto the mobile layouts, while the real viewport —
 * and therefore owner sheets / the bottom toolbar — stays desktop-sized.
 */
export function useStoreMobileCanvasPreview() {
  // Session cookie so SSR paints the phone column; sessionStorage alone left
  // the first paint desktop-wide until mount, then everything reflowed.
  const cookie = useCookie<string | number | null>(MOBILE_CANVAS_KEY, { default: () => null, sameSite: 'lax' })
  const active = useState<boolean>('store-mobile-canvas-preview', () => String(cookie.value) === '1')

  useHead({
    bodyAttrs: { class: computed(() => (active.value ? MOBILE_CANVAS_CLASS : '')) },
  })

  function isRealMobileViewport() {
    return import.meta.client && window.matchMedia('(max-width: 767.98px)').matches
  }

  function apply(on: boolean) {
    if (!import.meta.client) return
    const enabled = on && !isRealMobileViewport()
    document.body.classList.toggle(MOBILE_CANVAS_CLASS, enabled)
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'))
    })
  }

  function persist(on: boolean) {
    active.value = on
    cookie.value = on ? '1' : null
    apply(on)
    try {
      sessionStorage.setItem(MOBILE_CANVAS_KEY, on ? '1' : '0')
    }
    catch {
      // ignore
    }
  }

  function toggle() {
    persist(!active.value)
  }

  watch(active, (on) => apply(on), { immediate: true })

  onMounted(() => {
    if (isRealMobileViewport()) {
      persist(false)
      return
    }
    try {
      if (sessionStorage.getItem(MOBILE_CANVAS_KEY) === '1') {
        persist(true)
      }
    }
    catch {
      // ignore
    }
    const mq = window.matchMedia('(max-width: 767.98px)')
    const onViewport = () => {
      if (mq.matches) persist(false)
    }
    mq.addEventListener('change', onViewport)
    onBeforeUnmount(() => mq.removeEventListener('change', onViewport))
  })

  return {
    mobileCanvas: active,
    toggleMobileCanvas: toggle,
  }
}

/** Preview orders use client-side ids — never a real store_order row. */
export function isPreviewStoreOrder(orderId: string | null | undefined): boolean {
  return String(orderId || '').startsWith('preview-')
}

/**
 * Owners and super-admins in the design studio should walk through checkout
 * without creating real orders or consuming checkout quotas.
 */
export function useStorePreviewCheckout(
  store: MaybeRefOrGetter<OnlineStore | null | undefined>,
) {
  const { user } = useAuth()
  const livePreview = useStoreLivePreview()
  const { isStudio, isPreview } = useStoreAdminAccess()

  return computed(() => {
    if (livePreview.value) return false
    if (isStudio.value || isPreview.value) return true
    const row = toValue(store)
    return Boolean(row && hasStorePermission(row, user.value, 'design'))
  })
}
